import { CollectionConfig } from "@rebasepro/types";
import jobsCollection from "./jobs";
import companiesCollection from "./companies";
import talentsCollection from "./talents";
import { APPLICATION_STATUSES } from "../application_status";


/**
 * `status_notified_*` belong to the server, whoever sends them.
 *
 * They record that a letter left (drizzle/0065), and the rule deciding whether
 * the next move owes one reads them: a row whose `status_notified_status`
 * already says "rejected" gets no "Descartado" letter. So a caller who could
 * write them could do two things this product must not allow — show "Avisado"
 * for a letter that never went, or move a candidate to "Descartado" without
 * them hearing. `company_advance_own_applications` lets a company UPDATE the
 * whole row; this is what narrows it back for these two columns.
 *
 * Pinned rather than refused where it can be: the panel's own entity form
 * sends every property back, unchanged, on an ordinary save, and a save that
 * failed over a field nobody touched would be a worse bug than the one this
 * prevents. The server's own stamp does not come through here — it is a plain
 * UPDATE after the mail server answered (services/application-status-notice.ts).
 */
const NOTICE_FIELDS = ["status_notified_at", "status_notified_status"] as const;

function sameNoticeValue(a: unknown, b: unknown): boolean {
    if ((a ?? null) === (b ?? null)) return true;
    const ta = typeof a === "string" || a instanceof Date ? new Date(a).getTime() : NaN;
    const tb = typeof b === "string" || b instanceof Date ? new Date(b).getTime() : NaN;
    return !Number.isNaN(ta) && ta === tb;
}

function pinStatusNotice<V extends object>(
    values: V,
    previousValues: object | undefined,
    status: string | undefined
): V {
    const draft = values as Record<string, unknown>;
    const sent = NOTICE_FIELDS.filter(field => field in draft);
    if (sent.length === 0) return values;

    if (status !== "existing") {
        return { ...values, status_notified_at: null, status_notified_status: null };
    }

    const previous = previousValues as Record<string, unknown> | undefined;
    if (!previous) {
        throw new Error("El aviso al candidato lo registra el servidor cuando el correo sale; no se puede escribir a mano.");
    }
    if (sent.every(field => sameNoticeValue(draft[field], previous[field]))) return values;

    return {
        ...values,
        status_notified_at: previous.status_notified_at ?? null,
        status_notified_status: previous.status_notified_status ?? null
    };
}

const talentApplicationsCollection: CollectionConfig = {
    name: "Postulaciones",
    singularName: "Postulación",
    slug: "talent-applications",
    table: "talent_applications",
    callbacks: {
        /**
         * Fill in `talent_id` from the identity the caller authenticated as.
         *
         * The row carries two names for one person. `auth_user_id` is what RLS
         * authorises against and it points into the `rebase` schema, which no
         * collection can join to; `talent_id` (drizzle/0029) is what every
         * screen reads. The public apply path only ever knew about the first,
         * so applications arrived with `talent_id` NULL and the Postulaciones
         * panel rendered a registered candidate as "(candidato sin perfil)".
         *
         * Derived here rather than at the caller because there is more than one
         * door — the site's apply button, the admin creating a row by hand,
         * whatever comes next — and a field each of them has to remember
         * separately is a field one of them will forget. This is the seam they
         * all pass through.
         *
         * Three deliberate refusals:
         *
         *   - **A save without `auth_user_id` is left untouched.** The admin
         *     panel moves an application to "Entrevista" by sending `{ status }`
         *     alone; treating that as "resolve to nothing" would write NULL and
         *     orphan the row on every status change.
         *   - **An explicit `talent_id` wins.** If a caller says who this is, it
         *     is not this callback's place to overrule them, and the lookup is
         *     skipped entirely.
         *   - **A failed lookup is not a failed application.** `context.data`
         *     runs at the caller's privilege, so the read happens under the
         *     candidate's own RLS — `talent_own_row` lets them see themselves,
         *     but a policy change or an outage must not turn "your application
         *     was recorded" into "your application was rejected". An unlinked
         *     row is a card that says so; a throw is a person who could not
         *     apply.
         *
         * The same seam carries a second rule: `pinStatusNotice`, above.
         *
         * **Telling the candidate is not here.** A status change sends them a
         * letter, and it is the same "more than one door" problem — but mail
         * is backend code and this file is bundled into the admin panel, and
         * `beforeSave` runs before a write that can still fail. It is a global
         * `afterSave` in `backend/src/index.ts` that waits for the commit
         * (`services/application-status-notice.ts`).
         */
        beforeSave: async ({ values: incoming, previousValues, status, context }) => {
            const values = pinStatusNotice(incoming, previousValues, status);
            const draft = values as Record<string, unknown>;
            if (draft.talent_id) return values;

            const authUserId = typeof draft.auth_user_id === "string" ? draft.auth_user_id : "";
            if (!authUserId) return values;

            try {
                const found = await context.data.talents.find({
                    where: { auth_user_id: ["==", authUserId] },
                    limit: 1
                });
                const talent = (found?.data ?? [])[0] as { id?: unknown } | undefined;
                if (talent?.id) {
                    return { ...values, talent_id: String(talent.id) };
                }
            } catch (err) {
                console.error("[talent_applications] no se pudo resolver talent_id", err);
            }

            return values;
        }
    },
    securityRules: [
        {
            name: "talent_own_applications",
            operation: "all",
            using: "auth.uid() = auth_user_id",
            withCheck: "auth.uid() = auth_user_id"
        },
        {
            // A company sees applications addressed to it, and nothing else.
            name: "company_read_own_applications",
            operation: "select",
            using: "EXISTS (SELECT 1 FROM company_members cm WHERE cm.auth_user_id = auth.uid() AND cm.company_id = talent_applications.company_id)"
        },
        {
            // …and moves them through its own hiring pipeline. This is the
            // write half of the self-serve ATS: previously a company could read
            // an application but never act on it, so the status enum
            // (applied → reviewing → interview → rejected/hired) could only
            // ever be advanced by an admin in the CMS.
            //
            // `withCheck` re-states the membership test rather than trusting
            // `using`: on an UPDATE, `using` authorises the row being read and
            // `withCheck` the row being written, and without the second a
            // member could reassign `company_id` and move an application into
            // another company.
            //
            // What neither can do is pin a *column*. RLS decides whether a row
            // may be written, never which of its fields changed, and Rebase has
            // no column-level write rule (`admin.readOnly` is a form setting
            // the API does not enforce). So this policy alone let a company
            // rewrite the candidate's `message` — or set `auth_user_id` to any
            // other account, which `company_read_own_applicants` on talents
            // joins on, opening that person's row and CV. That is enforced by
            // the trigger `talent_applications_only_status_for_others`
            // (drizzle/0064, mirrored in scripts/setup-local-db.sh): a caller
            // who is neither the applicant nor an admin may change `status`
            // and nothing else.
            name: "company_advance_own_applications",
            operation: "update",
            using: "EXISTS (SELECT 1 FROM company_members cm WHERE cm.auth_user_id = auth.uid() AND cm.company_id = talent_applications.company_id)",
            withCheck: "EXISTS (SELECT 1 FROM company_members cm WHERE cm.auth_user_id = auth.uid() AND cm.company_id = talent_applications.company_id)"
        },
        {
            name: "admin_full_access",
            operation: "all",
            roles: ["admin"]
        }
    ],
    properties: {
        id: {
            name: "ID",
            type: "string",
            isId: "uuid"
        },
        job_id: {
            name: "Vacante",
            type: "relation",
            relation: {
                kind: "belongsTo",
                target: () => jobsCollection,
                localKey: "job_id"
            },
            validation: { required: true },
            description: "La vacante a la que corresponde esta postulación"
        },
        company_id: {
            name: "Empresa",
            type: "relation",
            relation: {
                kind: "belongsTo",
                target: () => companiesCollection,
                localKey: "company_id"
            },
            description: "La empresa que publica la vacante"
        },
        // Who applied. Added by drizzle/0029 because the row had no way to say
        // it: `auth_user_id` points into the `rebase` schema, which the list
        // renderer resolves to nothing, so every row showed a uuid where a name
        // belongs — the reason this collection used to be hidden from the
        // drawer. All 41 applications resolve to exactly one talent.
        talent_id: {
            name: "Candidato",
            type: "relation",
            relation: {
                kind: "belongsTo",
                target: () => talentsCollection,
                localKey: "talent_id"
            },
            description: "La persona que se postuló"
        },
        // The identity RLS authorises against (`auth.uid() = auth_user_id`),
        // kept as a `userSelect` string. `talent_id` is the display path; this
        // is the one that decides who may read the row, and a candidate who
        // applied before completing a talent profile has this and not that.
        auth_user_id: {
            name: "Cuenta de usuario",
            type: "string",
            userSelect: true,
            // Not required since 0051: an application registered from an email
            // in Correo names the person through `talent_id` and has no
            // account behind it. The database's CHECK insists on one or the
            // other, so a row that names nobody is still refused.
            description: "Usuario que se postuló. Vacío cuando la candidatura se registró desde un correo: entonces la persona es el candidato.",
            // Identity is pinned: for a company by the trigger in drizzle/0064
            // (not by the policy's `withCheck`, which cannot see columns), so an
            // application cannot be reassigned to another person. The admin form
            // should not offer what the database refuses — and an admin bypasses
            // both, so this is the only guard on that side.
            admin: { readOnly: true }
        },
        /**
         * What the candidate wrote to the employer when applying.
         *
         * An application used to be three ids and a status, so the company
         * learned a name and an address and nothing about why this person.
         * Optional, and NULL when they wrote nothing — never an empty string
         * standing in for a note.
         *
         * Their words, so nobody else edits them: read-only in this form, and
         * for a company refused by the database (drizzle/0064). 600 characters
         * is the form's limit and the table's CHECK, so the two cannot drift.
         */
        message: {
            name: "Nota de la persona",
            type: "string",
            validation: { max: 600 },
            description: "Lo que la persona escribió a la empresa al postularse. Opcional; solo la persona puede escribirlo.",
            admin: { multiline: true, readOnly: true }
        },
        source: {
            name: "Origen",
            type: "string",
            enum: [
                { id: "site", label: "Desde la web", color: "green" },
                { id: "email", label: "Desde un correo", color: "cyan" },
                { id: "manual", label: "A mano", color: "grayLight" }
            ],
            defaultValue: "site",
            description: "Cómo llegó la candidatura. Desde un correo = un operador la registró en Correo a partir de lo que la persona escribió.",
            admin: { readOnly: true }
        },
        status: {
            name: "Estado",
            type: "string",
            // Declared in `config/application_status.ts`, which the employer
            // dashboard on the site imports too: the two lists had drifted
            // until the site offered a "Pendiente" this column cannot hold and
            // never offered "Entrevista".
            enum: APPLICATION_STATUSES.map(option => ({ ...option })),
            defaultValue: "applied"
        },
        created_at: {
            name: "Creado",
            type: "date",
            autoValue: "on_create"
        },
        // drizzle/0065. What the candidate was last told, written by the
        // backend only after the mail server accepted the letter. Read-only
        // here, and pinned in `beforeSave` for every other writer.
        status_notified_at: {
            name: "Avisado el",
            type: "date",
            description: "Cuándo le escribimos al candidato para contarle en qué punto está su candidatura. Sólo se rellena si el correo salió.",
            admin: { readOnly: true }
        },
        status_notified_status: {
            name: "Avisado de",
            type: "string",
            // The same enum as `status`, in the database and here.
            enum: APPLICATION_STATUSES.map(option => ({ ...option })),
            description: "El estado del que trataba ese correo. Si ya no coincide con el estado actual, la persona todavía no sabe del último cambio.",
            admin: { readOnly: true }
        }
    },
    admin: {
        group: "Personas",
        icon: "ClipboardList",
        // Out of the menu. The same 47 rows are worked from the candidate's
        // record and from the vacancy's "Postulaciones" tab, and this was a
        // third door to the same work — the raw table, where changing a
        // status writes to nobody. Still reachable by URL and from the
        // relations that point here.
        hideFromNavigation: true,
        // This was `hideFromNavigation: true`, on the grounds that a row could
        // only ever render the applicant's uuid — so the table was reachable
        // solely from a card on the home page, which is not somewhere anyone
        // thinks to look for a top-level noun. drizzle/0029 gave the row a
        // `talent_id`, so it can name the person now and belongs in the drawer.
        sort: ["created_at", "desc"],
        // `job_id` is absent on purpose: the list renders it as each row's
        // subtitle via `display`, and leaving it here printed the same vacancy
        // title twice on every row — once under the name and once as a column.
        // Same reason `company_name` is missing from the jobs list.
        listProperties: ["talent_id", "status", "source", "created_at"],
        /**
         * The person names the row, and what they applied to sits under it.
         *
         * An application carries no text of its own — three relation ids, a user
         * uuid, a status and a timestamp — so the derived title fell through to
         * the first enum it found and every row read "Postulado", the same word
         * already printed in its own column beside it, over the applicant's raw
         * `auth_user_id`. `titleProperty` and `previewProperties` could not fix
         * it: both rank properties, and the derivation skips a relation.
         *
         * Title is the candidate and not the vacancy. The question this table
         * answers is "who applied", and it is the question its own name asks —
         * the vacancy is the answer to "to what", which is what a subtitle is
         * for. It also stops the list reading as a column of repeated job
         * titles: 37 of the 41 rows point at just two vacancies.
         *
         * These are declared paths and not resolvers on purpose. The list
         * already asks for `include: ["*"]` whenever a collection has relations
         * (`getRelationIncludeParams`), so the talent and the job arrive in the
         * same request as the rows — a resolver would re-fetch per row what is
         * already in hand. A path also renders through the property's own
         * preview, so each shows as the target's own title (the person's
         * `full_name`, the vacancy's `title`) as linked text rather than a uuid.
         */
        display: {
            title: "talent_id",
            subtitle: "job_id",
            status: "status",
            date: "created_at"
        },
        // Only the nested-preview shape now — what an application looks like when
        // *another* record references it. `display` above owns the row's title and
        // subtitle, and wins over these: `previewProperties` can only rank
        // properties of this row, which is what left every row reading "Postulado"
        // over a uuid before there was anywhere else to say it.
        previewProperties: ["talent_id", "status"]
    }
};

export default talentApplicationsCollection;
